import * as React from 'react';

type Options = {
    isHovered: boolean;
    isInstant?: boolean;
    delayMs?: number;
};

/**
 * Hook used to determine if an element is still hovered after a given delay.
 * If `isInstant` is true, the delay will be ignored.
 */
export function useDelayedHoverVisibility(options: Options): boolean {
    const {isHovered, isInstant = false, delayMs = 700} = options;

    const [isShown, setIsShown] = React.useState<boolean>(false);
    const timerRef = React.useRef<number | null | undefined>(null);

    React.useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (!isHovered) {
            if (isShown) {
                setIsShown(false);
            }

            return () => {};
        }

        if (isInstant) {
            if (!isShown) {
                setIsShown(true);
            }

            return () => {};
        }

        // @ts-expect-error
        timerRef.current = setTimeout(() => {
            if (isHovered) {
                setIsShown(true);
            }
        }, delayMs);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isHovered, isInstant, delayMs, isShown]);

    return isShown;
}
